import { invokeFunction, supabase, unwrap } from '@/services/supabase/client'
import type { MediaKind } from '@/types/models'

export interface UploadTicket {
  mediaId: string
  uploadUrl: string
  thumbnailUploadUrl: string | null
  headers: Record<string, string>
  expiresIn: number
}

export interface MediaVariantUrl {
  url: string
  expiresAt: number
}

/**
 * The storage seam. (§80)
 *
 * Everything above this interface talks about "a photograph"; only the
 * implementation below knows about buckets, signatures and expiry. Swapping the
 * provider later is a matter of writing a second class, not touching features.
 */
export interface MediaStorageProvider {
  requestUpload(input: {
    kind: MediaKind
    mimeType: string
    sizeBytes: number
    catId?: string | null
    sightingId?: string | null
    width?: number
    height?: number
    durationMs?: number
    /** Re-sign a placeholder from an earlier attempt instead of creating another. */
    mediaId?: string | null
  }): Promise<UploadTicket>

  upload(ticket: UploadTicket, blob: Blob, thumbnail?: Blob | null): Promise<void>

  finalize(input: {
    mediaId: string
    sizeBytes?: number
    width?: number
    height?: number
  }): Promise<void>

  getViewUrl(mediaId: string, variant: 'full' | 'thumb'): Promise<MediaVariantUrl>
}

/**
 * Cloudflare R2 behind a private bucket.
 *
 * Nothing in this class ever holds a permanent URL. Reading a photograph means
 * asking the backend, which checks the relationship first and then signs a URL
 * that stops working in minutes. A permanent public URL would be a privacy
 * failure that no amount of interface hiding could repair. (§79)
 */
export class R2MediaStorageProvider implements MediaStorageProvider {
  async requestUpload(input: Parameters<MediaStorageProvider['requestUpload']>[0]) {
    return invokeFunction<UploadTicket>('media-upload-url', {
      kind: input.kind,
      mimeType: input.mimeType,
      sizeBytes: input.sizeBytes,
      catId: input.catId ?? null,
      sightingId: input.sightingId ?? null,
      width: input.width ?? null,
      height: input.height ?? null,
      durationMs: input.durationMs ?? null,
      mediaId: input.mediaId ?? null,
    })
  }

  async upload(ticket: UploadTicket, blob: Blob, thumbnail?: Blob | null) {
    const put = async (url: string, body: Blob) => {
      const response = await fetch(url, {
        method: 'PUT',
        body,
        headers: { ...ticket.headers, 'Content-Type': body.type },
      })
      if (!response.ok) {
        throw new Error('La subida ha fallado (' + response.status + ').')
      }
    }

    await put(ticket.uploadUrl, blob)
    if (thumbnail && ticket.thumbnailUploadUrl) {
      // A missing thumbnail is a degraded gallery, not a lost photograph.
      await put(ticket.thumbnailUploadUrl, thumbnail).catch(() => undefined)
    }
  }

  async finalize(input: { mediaId: string; sizeBytes?: number; width?: number; height?: number }) {
    unwrap(
      await supabase.rpc('finalize_media', {
        p_media_id: input.mediaId,
        p_size_bytes: input.sizeBytes ?? null,
        p_width: input.width ?? null,
        p_height: input.height ?? null,
      }),
    )
  }

  async getViewUrl(mediaId: string, variant: 'full' | 'thumb') {
    return invokeFunction<MediaVariantUrl>('media-view-url', { mediaId, variant })
  }
}

export const mediaStorage: MediaStorageProvider = new R2MediaStorageProvider()

export async function attachMedia(mediaId: string, catId: string, sightingId?: string) {
  unwrap(
    await supabase.rpc('attach_media', {
      p_media_id: mediaId,
      p_cat_id: catId,
      p_sighting_id: sightingId ?? null,
    }),
  )
}

/** Deletes the photograph for real: the row AND the files in the bucket. */
export async function deleteMedia(mediaId: string) {
  await invokeFunction<{ ok: boolean }>('media-delete', { mediaId })
}
